import {useEffect, useMemo, useState} from 'react';
import {ProductPickerItem} from './types';

const tokenize = (value: string) =>
    value
        .toLowerCase()
        .split(/[\s,.;:/\\|_+-]+/)
        .map((part) => part.trim())
        .filter(Boolean);

export const buildProductSearchHaystack = (product: ProductPickerItem): string =>
    [
        String(product.id),
        product.name,
        product.part_number,
        product.brand,
        product.category,
        // description intentionally skipped — heavy and rarely useful for picker search
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

const getHaystack = (product: ProductPickerItem): string =>
    product.searchHaystack || buildProductSearchHaystack(product);

export const matchHaystackQuery = (haystack: string, query: string): boolean => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return true;
    const words = tokenize(normalized);
    if (words.length === 0) return true;
    return words.every((word) => haystack.includes(word));
};

export const matchProductQuery = (product: ProductPickerItem, query: string): boolean =>
    matchHaystackQuery(getHaystack(product), query);

export const filterProductsByQuery = (
    products: ProductPickerItem[],
    query: string,
): ProductPickerItem[] => {
    const trimmed = query.trim();
    if (!trimmed) return products;
    return products.filter((product) => matchProductQuery(product, trimmed));
};

const DEFAULT_DEBOUNCE_MS = 180;
const DEBOUNCE_THRESHOLD = 250;

export const useProductFilter = (
    products: ProductPickerItem[],
    initialQuery = '',
    options?: {debounceMs?: number},
) => {
    const [query, setQuery] = useState(initialQuery);
    const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);

    const debounceMs = options?.debounceMs
        ?? (products.length >= DEBOUNCE_THRESHOLD ? DEFAULT_DEBOUNCE_MS : 0);

    useEffect(() => {
        if (debounceMs <= 0) {
            setDebouncedQuery(query);
            return;
        }
        const timer = window.setTimeout(() => setDebouncedQuery(query), debounceMs);
        return () => window.clearTimeout(timer);
    }, [query, debounceMs]);

    const indexed = useMemo(
        () => products.map((product) => ({
            product,
            haystack: getHaystack(product),
        })),
        [products],
    );

    const results = useMemo(() => {
        const trimmed = debouncedQuery.trim().toLowerCase();
        if (!trimmed) return products;
        const words = tokenize(trimmed);
        if (words.length === 0) return products;
        return indexed
            .filter(({haystack}) => words.every((word) => haystack.includes(word)))
            .map(({product}) => product);
    }, [indexed, debouncedQuery, products]);

    const exactByCode = useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) return null;
        return (
            products.find((product) => String(product.id) === trimmed)
            || products.find((product) => (product.part_number || '').toLowerCase() === trimmed)
            || null
        );
    }, [products, query]);

    const isFiltering = query.trim() !== debouncedQuery.trim();

    return {
        query,
        setQuery,
        debouncedQuery,
        results,
        exactByCode,
        isFiltering,
        filterNow: (value: string) => filterProductsByQuery(products, value),
    };
};
